import { useMemo, useState } from "react";
import { Search, FileSpreadsheet, Eye } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { exportCandidatesCsv } from "@/lib/demo/cv";
import { CENTROS, TITULACION_LIST, shortTit } from "@/lib/demo/seed";
import { fullName, initials, DAY_LABELS, fmt, stripAccents } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const FilterSelect = ({ id, value, onChange, placeholder, options }) => {
  const { t } = useI18n();
  return (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 w-full rounded-xl sm:w-[190px]" data-testid={`candidates-filter-${id}`}><SelectValue placeholder={t(placeholder)} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{t("{label}: todos", { label: t(placeholder) })}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{t(l)}</SelectItem>)}
    </SelectContent>
  </Select>
  );
};

export const exportRows = (rows, t) => {
  exportCandidatesCsv(rows, "Bosch");
  toast.success(t("Exportación completada"), { description: t("{n} candidatos exportados a Excel (.csv).", { n: rows.length }) });
};

export const CandidatesTable = ({ rows, total, onOpen, onViewCv }) => {
  const [q, setQ] = useState("");
  const [tit, setTit] = useState("all");
  const [centro, setCentro] = useState("all");
  const [day, setDay] = useState("all");
  const { t } = useI18n();

  const filtered = useMemo(() => {
    const nq = stripAccents(q.toLowerCase().trim());
    return rows.filter(({ participant: p, interaction: i }) =>
      (!nq || stripAccents(`${fullName(p)} ${p.email}`.toLowerCase()).includes(nq)) &&
      (tit === "all" || p.titulacion === tit) && (centro === "all" || p.centro === centro) && (day === "all" || String(i.day) === day));
  }, [rows, q, tit, centro, day]);

  return (
    <div className="space-y-5" data-testid="candidates-section">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar candidato")} className="h-11 rounded-xl pl-11" data-testid="candidates-search-input" />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <FilterSelect id="titulacion" value={tit} onChange={setTit} placeholder="Titulación" options={TITULACION_LIST.map((t) => [t, shortTit(t)])} />
          <FilterSelect id="centro" value={centro} onChange={setCentro} placeholder="Centro" options={CENTROS.map((c) => [c, c])} />
          <FilterSelect id="dia" value={day} onChange={setDay} placeholder="Día" options={[["1", "Día 1 · 12 mayo"], ["2", "Día 2 · 13 mayo"]]} />
        </div>
        <button onClick={() => exportRows(filtered, t)} className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 text-sm font-bold tracking-wide text-white transition-colors hover:bg-emerald-800" data-testid="candidates-export-button">
          <FileSpreadsheet className="h-4 w-4" /> {t("EXPORTAR EXCEL")}
        </button>
      </div>
      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["table|Nombre", "Titulación", "Centro", "Fecha", "Hora", "CV"].map((h) => <TableHead key={h} className="h-12 text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(h)}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const { participant: p, interaction: i } = r;
                const fresh = i.id.startsWith("live-");
                return (
                  <TableRow key={i.id} onClick={() => onOpen(r)} className={`cursor-pointer ${fresh ? "bg-upv-light/60" : ""}`} data-testid={`candidate-row-${p.id}`}>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">{initials(p)}</span>
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 whitespace-nowrap font-semibold text-slate-900">{fullName(p)}{fresh && <span className="rounded-full bg-upv px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`candidate-new-badge-${p.id}`}>{t("NUEVO")}</span>}</p>
                          <p className="truncate text-xs text-slate-500">{p.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{t(shortTit(p.titulacion))}</TableCell>
                    <TableCell className="text-sm font-semibold">{p.centro}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{t(DAY_LABELS[i.day])}</TableCell>
                    <TableCell className="font-mono text-sm font-bold">{i.time}</TableCell>
                    <TableCell>
                      <button onClick={(e) => { e.stopPropagation(); onViewCv(p); }} className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-800 transition-colors hover:border-upv hover:text-upv" data-testid={`candidate-view-cv-${p.id}`}>
                        <Eye className="h-3.5 w-3.5" /> {t("VER CV")}
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500" data-testid="candidates-empty">{t("No hay candidatos que coincidan con los filtros.")}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-400" data-testid="candidates-count">{t("Mostrando {n} perfiles de demostración · {total} candidatos recibidos en total", { n: filtered.length, total: fmt(total) })}</p>
      </div>
    </div>
  );
};
